/**
 * 제주 행정구역 데이터 + RegionChips chip → adm_nm 매칭 로직.
 *
 * 데이터: src/data/jeju-regions.json (vuski/admdongkor ver20260401, 제주만 추출)
 * 단위: 행정동(읍/면/동), 좌표계 WGS84
 */
import jejuRegions from "../data/jeju-regions.json";

/**
 * chip key → adm_nm 매칭 함수.
 * RegionChips 의 chip key 와 1:1 대응.
 *
 * "제주시" / "서귀포시" chip 은 시 단위가 아니라 "시 직할 동" 들만 의미
 * (애월/한림/조천/구좌 등은 별도 chip 이므로).
 */
const REGION_MATCHERS = {
  jeju: (admNm) =>
    admNm.startsWith("제주특별자치도 제주시 ") && admNm.endsWith("동"),
  aewol: (admNm) => admNm.endsWith("애월읍"),
  hallim: (admNm) => admNm.endsWith("한림읍") || admNm.endsWith("한경면"),
  jocheon: (admNm) => admNm.endsWith("조천읍"),
  gujwa: (admNm) => admNm.endsWith("구좌읍"),
  chuja: (admNm) => admNm.endsWith("추자면"),
  udo: (admNm) => admNm.endsWith("우도면"),
  seogwipo: (admNm) =>
    admNm.startsWith("제주특별자치도 서귀포시 ") && admNm.endsWith("동"),
  daejeong: (admNm) => admNm.endsWith("대정읍"),
  andeok: (admNm) => admNm.endsWith("안덕면"),
  namwon: (admNm) => admNm.endsWith("남원읍"),
  seongsan: (admNm) => admNm.endsWith("성산읍"),
  pyoseon: (admNm) => admNm.endsWith("표선면"),
};

/** chip key 로 해당 region 의 GeoJSON features 배열을 반환. */
export function getRegionFeatures(regionKey) {
  const matcher = REGION_MATCHERS[regionKey];
  if (!matcher) return [];
  return jejuRegions.features.filter((f) =>
    matcher(f.properties.adm_nm || "")
  );
}

/**
 * features 의 모든 좌표를 훑어서 bounding box 계산.
 * Polygon / MultiPolygon 모두 처리.
 */
export function computeBounds(features) {
  let minLng = Infinity,
    maxLng = -Infinity,
    minLat = Infinity,
    maxLat = -Infinity;

  const visit = (coords) => {
    // coords 는 [lng, lat] 단일 점 또는 그것의 배열
    if (typeof coords[0] === "number") {
      const [lng, lat] = coords;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    } else {
      for (const c of coords) visit(c);
    }
  };

  for (const f of features) {
    visit(f.geometry.coordinates);
  }

  return { minLng, maxLng, minLat, maxLat };
}

/**
 * GeoJSON feature 의 geometry 를 네이버 지도 Polygon paths 로 변환.
 * Polygon → [LatLng[]][] (1개의 polygon)
 * MultiPolygon → [LatLng[]][][] 같은 구조이지만 각 polygon 마다 따로 그려야 함.
 *
 * 반환: { type, polygons: [[ring1, ring2, ...], ...] }
 *   - polygons 배열의 각 원소가 별도의 네이버 지도 Polygon
 *   - 각 polygon 의 첫 ring 은 외곽, 나머지는 hole
 *   - ring 은 LatLng 객체의 배열
 */
export function geometryToNaverPaths(geometry, naverMaps) {
  const { LatLng } = naverMaps;
  const toLatLng = ([lng, lat]) => new LatLng(lat, lng);
  const ringToPath = (ring) => ring.map(toLatLng);

  if (geometry.type === "Polygon") {
    return [geometry.coordinates.map(ringToPath)];
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.map((poly) => poly.map(ringToPath));
  }
  return [];
}
